import React, { useState, useEffect } from 'react';

const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Card data with images only
  const floatingCards = [
    {
      id: 1,
      image: '/images/seamless_cloud_migrations.png',
    },
    {
      id: 2,
      image: '/images/devops_optimization_service.png',
    },
    {
      id: 3,
      image: '/images/finops_expertise.png',
    },
    {
      id: 4,
      image: '/images/devsecops.png',
    },
    {
      id: 5,
      image: '/images/disaster_recovery.png',
    },
    {
      id: 6,
      image: '/images/cloud_native_kubernetes.png',
    },
    {
      id: 7,
      image: '/images/hld_lld.png',
    },
    {
      id: 8,
      image: '/images/discount.png',
    },
    {
      id: 9,
      image: '/images/support.png',
    },
    {
      id: 10,
      image: '/images/platform.png',
    },
    {
      id: 11,
      image: '/images/mlops_gpu.png',
    },
    {
      id: 12,
      image: '/images/robust_cicd.png',
    },
    {
      id: 13,
      image: '/images/etl_pipelines.png',
    }
  ];

  // Auto-rotate cards every 3 seconds with zoom effect
  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentCardIndex((prevIndex) => 
          (prevIndex + 1) % floatingCards.length
        );
        setIsTransitioning(false);
      }, 150); // Half of the transition duration
    }, 2000);

    return () => clearInterval(interval);
  }, [floatingCards.length]);

  const services = [
    {
      id: 'cloud-migrations',
      title: 'Cloud Migrations',
      description: `**Migrate to the cloud with confidence — without the chaos.**
      
Whether you're migrating from on-premise servers, legacy systems, or another cloud provider, we ensure your workloads transition smoothly to **AWS, Google Cloud Platform (GCP), or Microsoft Azure** — with **zero downtime** and **zero disruption** to your users.`,
      icon: '☁️',
      detailedDescription: `Our team handles the full migration lifecycle:
      
      • **Assessment & Strategy:** We evaluate your current infrastructure and create a customized migration roadmap tailored to your goals.
      • **Architecture & Planning:** We design scalable, secure, and cost-efficient cloud architectures aligned with industry best practices.
      • **Migration & Execution:** We automate the migration of servers, databases, applications, and services with rollback plans and testing.
      • **Validation & Optimization:** Post-migration, we optimize performance, implement monitoring, and ensure everything runs better than before.

      Whether you’re a startup looking to scale or an enterprise modernizing legacy systems, we reduce the risk, complexity, and cost of your move to the cloud.

      **No surprises. No guesswork. Just a smooth journey to the cloud — the RogerOps way.**
`
    },


    {
      id: 'devops-optimization',
      title: 'DevOps Optimization',
      description: `**Accelerate your software delivery with smarter, faster DevOps.** 
      In today’s competitive tech landscape, speed, stability, and scalability aren’t optional — they’re essential. At RogerOps, we help engineering teams optimize their DevOps pipelines so they can build, test, and ship software with greater confidence and velocity.
`,
      icon: '🚀',
      detailedDescription: `**We take a hands-on approach to modernizing your DevOps workflows, transforming fragmented processes into a fully automated, highly efficient delivery pipeline.**
      
      Our DevOps Optimization services include:

      • **Infrastructure as Code (IaC):** Provision and manage infrastructure reliably using tools like Terraform, Pulumi, and Ansible — enabling reproducibility and version control.
      • **Environment Standardization:** Eliminate drift and reduce bugs with consistent environments across development, staging, and production.
      • **Release Management:** Implement automated testing, canary deployments, and rollbacks for safer, more predictable releases.
      • **Monitoring & Observability:** Integrate logs, metrics, and alerts to catch issues early and recover faster.
      
      Whether you’re just starting with DevOps or need to fine-tune a mature pipeline, RogerOps gives you the **speed of a startup** with the **stability of an enterprise.**

      **Release faster. Fail less. Scale smarter. That’s DevOps done right — with RogerOps.**
      `
    },


    {
      id: 'finops-expertise',
      title: 'FinOps Expertise',
      description: `**Cut cloud costs — without cutting performance.**
      
      Cloud costs can spiral quickly if left unchecked. At RogerOps, we bring deep FinOps expertise to help you **optimize every dollar** spent on AWS, GCP, or Azure — turning your cloud infrastructure from a cost center into a growth enabler.
      `,
      icon: '💰',
      detailedDescription: `Our FinOps services are designed to give you complete **visibility**, **proactive control**, and **strategic savings** across your cloud environment.

      Here’s how we help:

      • **Cloud Spend Analysis:** We audit your current usage, spot idle resources, and identify areas of waste or overspending.
      • **Cost Allocation & Tagging:** We implement precise tagging strategies so you can attribute spend by team, product, or environment.
      • **Resource Optimization:** Rightsize instances, scale down unused services, and schedule resources based on demand.
      • **Savings Plans & Reserved Instances:** We help you leverage the right AWS/GCP/Azure discounts and commitments to lower your long-term costs.
      • **Automated Governance:** Set guardrails to control budget overruns and enforce cost policies in real-time.

      Whether you’re scaling up or looking to rein in your existing spend, RogerOps ensures you only pay for what you truly need — and get maximum value from every byte of compute.

      **No more billing surprises. Just clear, predictable, and efficient cloud spend.**
`
    },
    {
      id: 'devsecops',
      title: 'DevSecOps',
      description: `**Security built into every layer — from code to cloud.**

      In the cloud, security can’t be an afterthought. At RogerOps, we integrate DevSecOps practices across your infrastructure and development lifecycle to ensure your systems are secure, compliant, and resilient from day one.


`,
      icon: '🔒',
      detailedDescription: `We help you shift security left — embedding it into CI/CD pipelines, automating policy enforcement, and making secure development second nature for your teams.

      Our DevSecOps services include:

      • **Security-by-Design Architecture:** We build secure infrastructure and workflows using zero-trust principles and defense-in-depth strategies.
      • **Pipeline Security Integration:** Scan code, containers, and dependencies for vulnerabilities with every build.
      • **IAM & Access Control Hardening:** Enforce least-privilege access, centralized identity management, and role-based policies across your cloud environment.
      • **Compliance-Ready Frameworks:** We implement controls and configurations aligned with standards like ISO 27001, SOC 2, HIPAA, and GDPR.
      • **Automated Threat Detection:** Set up real-time alerts, anomaly detection, and cloud-native firewalls to catch issues before they escalate.
      • **Secret Management & Encryption:** Secure keys, tokens, secrets and environment-level encryption.

      With RogerOps, security isn’t a blocker — it’s a business enabler. We make it easy to innovate fast **without compromising trust, compliance, or peace of mind.**

      **From your first commit to your production environment, we secure every step.**
`
    },
    {
      id: 'disaster-recovery',
      title: 'Disaster Recovery',
      description: `**Stay prepared. Stay online. Always.**

      Downtime is more than a technical issue — it's a business risk. Whether caused by outages, cyberattacks, human error, or natural disasters, unexpected failures can severely impact operations, revenue, and reputation.
      
      At RogerOps, we build **disaster recovery solutions that protect your business** minimize disruption, and ensure critical systems are restored quickly and reliably — no matter what.
`,
      icon: '🛡️',
      detailedDescription: `We design and implement **customized DR strategies** tailored to your infrastructure, compliance needs, and recovery goals. Whether you need high availability, geo-redundancy, or rapid failover — we’ve got you covered.

      Here’s what we deliver:

      • **Business Impact & Risk Assessment:** We assess your systems, identify vulnerabilities, and define Recovery Time Objectives (RTO) and Recovery Point Objectives (RPO).
      • **Resilient Architecture Design:** We build fault-tolerant, multi-region, and highly available infrastructure on AWS, GCP, or Azure.
      • **Automated Backups & Replication:** Continuous data protection through real-time replication, versioning, and encrypted backups.
      • **Failover & Recovery Workflows:** Fully automated failover mechanisms with playbooks and runbooks to recover services within minutes.
      • **DR Drills & Testing:** Regular disaster simulations to ensure your team and systems are always ready when it matters most.
      • **Compliance & Governance Alignment:** DR planning tailored to meet industry regulations like SOC 2, HIPAA, ISO, and more.

      With RogerOps, you're not just reacting to incidents — you're **proactively prepared.** We make disaster recovery painless, practical, and production-ready.

      **Because resilience isn’t optional — it’s a competitive edge.**
`
    },
    {
      id: 'kubernetes-expertise',
      title: 'Cloud Native Kubernetes Solutions',
      description: `**Orchestrate with confidence. Scale without limits.**

      At RogerOps, we empower your business to build and run scalable, resilient, and cloud-native applications using Kubernetes — the industry-standard container orchestration platform. Whether you're deploying microservices, managing high-traffic APIs, or handling complex workloads, we make Kubernetes work for you, not against you.
      We specialize in **multi-cluster management**, **auto-scaling**, and **high availability** configurations.`,
      icon: '⚓',
      detailedDescription: `**Our Kubernetes experts help you design, deploy, and manage production-grade clusters that are secure, self-healing, and built to scale.**

      Here’s what we offer in our end-to-end Kubernetes solutions:

      • **Elastic Scalability:** Leveraging and Configuring Cluster Autoscaler and KEDA **(Kubernetes-based Event Driven Autoscaler)** to dynamically scale workloads based on real-time events like queue length, CPU, memory, or custom metrics — ideal for asynchronous and event-driven architectures.
      • **Microservices-Ready Architecture:** Architect your platform around microservices using clean modular designs. Integrate **Istio service mesh** for fine-grained traffic control, security, and observability between services — with features like mutual TLS, retries, circuit breakers, and traffic shifting.
      • **Production-Grade Setup:** Deploy on any cloud (AWS EKS, GCP GKE, Azure AKS) or on-prem with proper role-based access control (RBAC), secrets management, and logging. Enable **self-healing, rolling deployments, and blue-green/canary strategies** for zero-downtime updates.
      • **Security & Observability:** Harden clusters with network policies, PodSecurity Standards, and image scanning. Implement full observability with Prometheus, Grafana, Loki, and Jaeger — so you can monitor, trace, and debug with clarity.

      With RogerOps, you don’t just get Kubernetes — you get a cloud-native platform tailored to your business, built with **scalability, security, and performance** at its core.

      **From single clusters to multi-region, multi-tenant deployments — we turn Kubernetes into your competitive advantage.**
`
    },


    {
      id: 'solution-architecting',
      title: 'Solution Architecting (HLD + LLD)',
      description: `**From big-picture strategy to low-level execution — we architect systems built to scale, perform, and evolve.**

      At RogerOps, we design cloud-native infrastructure and application architectures that are scalable, secure, and resilient. Whether you're building a new platform or modernizing legacy systems, we provide both **High-Level Design (HLD)** and **Low-Level Design (LLD)** to bring clarity and control to your tech landscape.
`,
      icon: '🏗️',
      detailedDescription: `📐 **What’s Included**
      
      **High-Level Design (HLD):**
      • Infrastructure & **application architecture** aligned with business goals
      • Cloud provider & compute model selection (AWS, Azure, GCP, K8s, Serverless)
      • Microservices layout, system boundaries, and inter-service communication
      • Network topology, security zones, and cost-performance modeling
      
      **Low-Level Design (LLD):**
      • Detailed VPC, subnet, routing, and IAM configuration
      • CI/CD pipeline design, container build specs, and deployment workflows
      • **Application-level details:** API gateway setup, database schema mapping, caching strategy, data flow diagrams
      • Monitoring, logging, alerting, and secret management integration

      🛠️ **Why It Matters**
      • **Clarity & speed:** Faster development with fewer surprises
      • **Built for scale:** Systems evolve without performance bottlenecks
      • **Optimized costs:** Right-sized design from the start
      • **Future-ready:** Easily adapt to new features, teams, and traffic

      Whether you're building from scratch or transforming legacy systems, RogerOps gives you a solid blueprint that makes execution seamless.

      **Think big. Design smart. Build right — with RogerOps at the core.**
`
    },
    {
      id: 'aws-discounts',
      title: 'Discounted Pricing for AWS',
      description: `**Same Cloud. Better Price. No Strings.**.
      
      We help you unlock exclusive AWS pricing — without sacrificing performance, security, or flexibility. Whether you're running production workloads, scaling distributed systems, or managing complex architectures, we make sure your cloud costs are optimized to the bone.
`,
      icon: '💳',
      detailedDescription: `With RogerOps, you get access to volume-discounted AWS rates even if you're a startup or mid-sized team. No need to commit to multi-year contracts or spend hours negotiating — we’ve already done that work for you.

      **What You Get:
      • Reduced pricing on EC2, RDS, S3, and more
      • No changes to your current infrastructure or architecture
      • Full transparency with zero hidden charges
      • Works seamlessly with existing Reserved Instances and Savings Plans
      • Real-time cost tracking and alerts

      **It’s the same AWS you trust — just smarter, leaner, and more affordable.**
      Coming soon: similar benefits for **Azure** and **Google Cloud Platform (GCP).**
`
    },
    {
      id: '24-7-support',
      title: '24/7 Support',
      description: `**Always On. Always Ready.**
      
      At RogerOps, we believe that your infrastructure deserves more than just good uptime — it deserves peace of mind. That’s why our dedicated support team is available 24/7/365, monitoring your systems and resolving issues before they ever become a problem.`,
      icon: '🕐',
      detailedDescription: `Whether it’s 2 PM or 2 AM, we’re here — watching, responding, and fixing in real time. Our engineers don’t just react; they proactively identify risks, implement solutions, and ensure your business never skips a beat.

      🛠️ **What You Can Expect:**
      • Round-the-clock system monitoring and incident response
      • Proactive issue resolution — often before you even notice
      • Fast SLAs, with real engineers (not bots) at the helm
      • On-call escalation for critical workloads
      • Deep expertise across AWS, Kubernetes, containers, databases, and more

      **Sleep easy. We've got your back — every second of every day.**
      With RogerOps, your infrastructure isn’t just supported — it’s guarded by experts.
      `
    },
    {
      id: 'infra-monitoring',
      title: 'Infrastructure Monitoring',
      description: `**Complete Visibility. Intelligent Insights. Zero Blind Spots.**

      In today's fast-paced digital environment, real-time visibility into your infrastructure is not a luxury — it's a necessity. At RogerOps, we empower you with comprehensive monitoring solutions that track every layer of your cloud infrastructure — from compute and storage to applications, databases, and containers.
      `,
      icon: '📊',
      detailedDescription: `We design and implement robust monitoring systems tailored to your architecture, so you’re always one step ahead. Our dashboards and alerts don’t just show metrics — they tell a story, uncovering the root cause of issues before they impact your business.

      📊 **Key Highlights:**
      • Real-time monitoring of servers, applications, and services
      • Custom dashboards tailored to your business KPIs
      • Intelligent alerting to avoid alert fatigue and missed signals
      • End-to-end observability across your stack: AWS, Kubernetes, VMs, DBs, and more
      • Seamless integration with tools like Prometheus, Grafana, New Relic, Datadog, and ELK stack

      From latency spikes to memory leaks — nothing slips through the cracks.
      **With RogerOps, you're not just monitoring — you're mastering your infrastructure.**
      `
    },
    {
      id: 'platform-analysis',
      title: 'Custom Platform Analysis (Analytics)',
      description: `**Turn Data into Decisions. Empower Every Click.**

      At RogerOps, we go beyond traditional dashboards — we transform raw logs, metrics, and performance data into actionable insights tailored specifically to your business needs.
`,
      icon: '🔍',
      detailedDescription: `Our custom analytics engine dives deep into your infrastructure and application stack to uncover patterns, bottlenecks, and opportunities that generic tools often miss. From slow response times to resource usage trends, we build clear, tailored dashboards that highlight what truly matters to your operations.

      🎯 **What We Offer:**
      • End-to-end data collection from infrastructure, apps, and user behavior
      • Business-specific KPIs and health checks designed with your goals in mind
      • Custom dashboards and reports using tools like Grafana, Kibana, and Datadog
      • Predictive analytics to forecast system load, downtime risk, and cost anomalies
      • Visualizations built for executives, engineers, and decision-makers alike

      By aligning analytics with your exact business context, we help you make faster, smarter, and more informed decisions — in real time.

      **At RogerOps, analytics isn’t just about data — it’s about delivering the story your infrastructure is trying to tell.**
`
    },
    {
      id: 'mlops-gpus',
      title: 'MLOps & GPUs',
      description: `**Power Smarter Models. Deliver AI at Scale.**

      At RogerOps, we don’t just provide infrastructure — we architect seamless MLOps pipelines powered by high-performance GPUs, enabling you to build, train, deploy, and monitor machine learning models efficiently and cost-effectively.
`,
      icon: '🤖',
      detailedDescription: `From data scientists to DevOps teams, we make AI/ML development faster, more scalable, and production-ready with a platform optimized for experimentation, collaboration, and reliability.

      **What We Offer:**
      • High-performance GPU clusters optimized for model training and inference (NLP, CV, DL, etc.)
      • End-to-end MLOps workflows — from data prep to CI/CD for ML models
      • Model versioning, tracking, and reproducibility using tools like MLflow, Kubeflow, and Weights & Biases
      • Automated deployment pipelines for scalable real-time and batch inference
      • Monitoring and alerting for drift detection, performance drop, and model accuracy in production
      • Seamless integration with your data lakes, cloud storage, and CI/CD tools

      By giving you easy and affordable access to GPU-powered MLOps capabilities, RogerOps ensures your AI initiatives move from idea to impact — at speed, and at scale.
`
    },
    {
      id: 'cicd-pipelines',
      title: 'Robust & Extensive CI/CD Pipelines',
      description: `**Ship Code Faster. With Confidence.**

      At RogerOps, we build and manage scalable CI/CD pipelines tailored to your team's workflow — enabling faster deployments, higher reliability, and greater developer productivity. Whether you're deploying microservices, ML models, or containerized apps, our pipelines ensure your code gets from dev to production securely and seamlessly.
`,
      icon: '⚡',
      detailedDescription: `We take care of the complexity so you can focus on building.

      **What We Offer:**
      • **Custom CI/CD pipelines** built using tools like GitLab CI, GitHub Actions, Jenkins, and Argo CD
      • **Support for containerized & cloud-native apps** (Kubernetes, Docker, Helm)
      • **Infrastructure as Code (IaC)** integration with Terraform, Pulumi, or CloudFormation
      • **Automated testing, security scans, and linting** integrated into your pipeline
      • **Progressive delivery support** — canary, blue-green, and rolling updates
      • **Observability & rollback mechanisms** for safe, trackable releases

      With RogerOps, your release cycles become shorter, more stable, and fully automated — helping you ship better software, faster.
`
    },
    {
      id: 'data-pipelines',
      title: 'Data Pipelines (ETL, Warehouse, Data Warehouse)',
      description: `**Streamline Your Data. Accelerate Decisions.**

      At RogerOps, we architect reliable and scalable data pipelines that power everything from dashboards to machine learning models. Whether it's real-time event data or batch processes, we help you ingest, transform, and store data in a way that's fast, clean, and ready for action.
`,
      icon: '📈',
      detailedDescription: `From raw logs to refined insights — we make your data work harder.

      🔄 **What We Offer:**
      • End-to-end ETL/ELT pipelines to extract, transform, and load data efficiently
      • Support for modern data stacks including Apache Airflow, dbt, Kafka, and Spark
      • Data warehouse integrations with BigQuery, Redshift, Snowflake, and Postgres
      • Automated data validation & cleansing to ensure high data quality
      • Real-time & batch processing pipelines tailored to your use case
      • Secure and scalable architecture designed for analytics, BI, and ML applications

      Whether you're building a reporting system or feeding real-time models, RogerOps delivers pipelines that are resilient, observable, and future-proof.
`
    }
  ];

  const currentCard = floatingCards[currentCardIndex];

  // Function to parse bold text formatting
  const parseBoldText = (text) => {
    return text.split('\n').map((line, index) => (
      <div key={index} className="mb-2">
        {line.split('**').map((part, partIndex) => 
          partIndex % 2 === 0 ? part : <strong key={partIndex}>{part}</strong>
        )}
      </div>
    ));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative text-white overflow-hidden min-h-screen">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/photo-1667372283545-1261fb5c427a.png')", // Replace with your image path
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Minimal overlay for text readability */}
          <div className="absolute inset-0 bg-black/10"></div>
        </div>
        
        <div className="relative max-w-[83rem] mx-[1rem] sm:mx-[2rem] md:mx-[6rem] py-20 min-h-screen flex items-center">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center w-full">
            <div className={`pl-0 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 lg:mb-12">
                What We Deliver
              </h1>
              <div className="max-w-md">
                <p className="text-lg sm:text-xl mb-6 lg:mb-8 opacity-90">
                At RogerOps, we engineer smarter, faster, and more resilient cloud infrastructures for modern businesses. Whether you're scaling, optimizing, or building from scratch — our experts help you move with confidence.
                </p>
                <p className="text-base sm:text-lg mb-6 lg:mb-8 opacity-80">
                From Kubernetes to cost savings, and CI/CD to cloud migrations — we deliver the DevOps firepower and architectural insight to accelerate your growth.
                </p>
              </div>
              {/* <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Enter your company name"
                  className="px-6 py-3 rounded-lg text-gray-700 flex-1"
                />
                <button className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                  Take a tour
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div> */}
            </div>
            
            {/* Image-Only Floating Card */}
            <div className={`relative mt-10 lg:mt-20 lg:ml-auto lg:mr-0 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className={`bg-white rounded-lg shadow-xl overflow-hidden max-w-sm mx-auto lg:ml-auto lg:mr-0 transform transition-all duration-300 ${
                isTransitioning ? 'scale-110' : 'scale-100'
              } hover:scale-105`}>
                {/* Card Image Only */}
                <div className="relative h-64 sm:h-80 lg:h-[28rem] overflow-hidden">
                  <img 
                    src={currentCard.image} 
                    alt="Service showcase"
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-6 border-2 border-white rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </div>

      {/* Services Grid Section */}
      <div className="bg-gray-50 py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <div className="bg-white border border-gray-300 text-gray-700 px-4 sm:px-6 py-2 rounded-lg text-sm font-medium mb-4 sm:mb-6 inline-block">
              Our Core Services
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            We build, optimize, and secure the infrastructure behind today's fastest-moving teams.
            </h2>
            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-regular text-gray-900 mb-4 sm:mb-6">
            Cloud-native. Cost-efficient. Always deployment-ready.
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-12 lg:mb-20 relative">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer relative"
                onClick={() => setSelectedService(service)}
              >
                <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">{service.icon}</div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                <div className="text-gray-600 text-xs sm:text-sm">
                  {parseBoldText(service.description)}
                </div>
                
                {/* Floating Detailed Card */}
                {selectedService && selectedService.id === service.id && (
                  <div className="absolute z-50 bg-black rounded-lg shadow-xl p-4 sm:p-6 w-72 sm:w-80 border border-gray-700"
                       style={{
                         left: index % 4 >= 2 ? 'auto' : '100%',
                         right: index % 4 >= 2 ? '100%' : 'auto',
                         top: '0',
                         marginLeft: index % 4 >= 2 ? '-320px' : '12px',
                         marginRight: index % 4 >= 2 ? '12px' : '0'
                       }}>
                    <div className="flex items-center mb-3 sm:mb-4">
                      <div className="text-2xl sm:text-3xl mr-2 sm:mr-3">{selectedService.icon}</div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-white">{selectedService.title}</h3>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4">
                      {parseBoldText(selectedService.detailedDescription)}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        className="bg-gray-700 text-white px-3 sm:px-4 py-2 rounded text-xs sm:text-sm font-semibold hover:bg-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedService(null);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Let's discuss how we can help transform your development and operations.
          </p>
          <button className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors">
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage; 